<script>

export default {
  name: 'Pricing',

  props: {
    tariff: {
      type: Object,
      required: true
    },
    selectedCarSection: {
      type: Object
    }
  },

  data() {
    return {
      footnotes: {},
      tariffExpanded: false,
      hovering: false
    }
  },

  mounted() {
    if(!window.tariff) window.tariff = []
    var length = window.tariff.length
    window.tariff[length] = this.tariff

    this.addTrackingDataToRegisterButton({
      element: this.$el.querySelector('a.moqo_link'),
      section: 'pricing-table',
      tariff: this.selectedCarSection && this.selectedCarSection.name
    })
  },

  methods: {
    footnoteNumber(priceSlot) {
      if (!priceSlot.constraints) {
        return
      }

      if (!this.footnotes[priceSlot.constraints]) {
        this.footnotes[priceSlot.constraints] = Object.keys(this.footnotes).length + 1
      }

      return this.footnotes[priceSlot.constraints]
    }
  },

  computed: {
    sizeClass () {
      return [
        'small-12',
        `medium-${Math.floor(24 / Math.min(this.displayedTariffSlots.length, 3))}`,
        `mediumlarge-${Math.floor(24 / Math.min(this.displayedTariffSlots.length, 4))}`
      ]
    },

    vatRate () {
      return `${this.tariff.to_pay_per_km.vat_rate / 100.0} %`
    },

    anyNetPrice () {
      return this.tariff.to_pay_per_km.vat_status == 'net' ||
        this.tariffSlots.some( priceSlot => (priceSlot.to_pay_per_unit || priceSlot.to_pay_per_hour).vat_status == 'net')
    },

    baseFee () {
      if (!this.tariff.base_fee.cents) return null

      return {
        name: 'booking',
        to_pay_per_booking: this.tariff.base_fee
      }
    },

    signupBtnText() {
      if (this.selectedCarSection.signup_fee || this.selectedCarSection.subscription_costs) {
        return this.$t('teamsite.call_to_action.registration_btn')
      }
      return this.$t('teamsite.call_to_action.free_registration_btn')
    },

    tariffSlots() {
      return this.tariff.tariff_slots.filter( slot => (slot.to_pay_per_unit || slot.to_pay_per_hour || {}).cents > 0 )
    },

    displayedTariffSlots() {
      if (this.includeBaseFee) this.tariffSlots.unshift(this.baseFee)
      return this.tariffExpanded ? this.tariffSlots : this.tariffSlots.slice(0, 4)
    },

    tariffExpandable() {
      return this.tariffSlots.length > (this.includeBaseFee ? 3 : 4)
    },

    hoverClass(){
      if(!this.hovering) return []
      return this.tariffExpanded ? ['hover-up'] : ['hover-down']
    },

    includeBaseFee () {
      return this.baseFee && !this.tariff.detail_description
    }
  }
}
</script>

<template>
  <div>
    <div class="pricing row" :class="{padding: !tariffExpandable}">
      <div class="columns">
        <div class="row">
          <price-tag v-for="(priceSlot, index) in displayedTariffSlots"
                :priceSlot="priceSlot"
                :class="sizeClass"
                :key="index"
                :toPayPerKm="tariff.to_pay_per_km"
                :footnoteNumber="footnoteNumber(priceSlot)"
            >
          </price-tag>
        </div>
      </div>
      <div class="row">
        <div class="columns text-center">
          <span class="expand-tariff" :class="hoverClass" v-if="tariffExpandable"  @click="tariffExpanded = !tariffExpanded;hovering=false" @mouseover="hovering=true" @mouseout="hovering=false">
            <span class="text" v-if="!tariffExpanded" :style="{ 'background-color': teamsiteConfig.color }">{{ $t('pricing.all_tariffs') }}</span><br>
            <span class="arrow" :style="{ color: teamsiteConfig.color }"> {{ tariffExpanded ? '↑' : '↓' }} </span>
          </span>
        </div>
      </div>
      <div v-if='tariff.detail_description' class="row">
        <div class="column medium-22 medium-offset-1">
          <p class="constraints-hint">{{ tariff.detail_description }}</p>
        </div>
      </div>
    </div>

    <div class="car-cta row" v-if="selectedCarSection">
      <div class="column medium-16 text-center" v-html="$t('cars.promotext', {
          productName: selectedCarSection.name,
          signupConditions: signup_fee_text(selectedCarSection),
          subscriptionConditions: subscription_fee_text(selectedCarSection)
        })">
      </div>
      <div class="column medium-8 text-right">
        <mq-link :destination="signUpSrc(selectedCarSection.signup_url)" size="tiny" type="button" :color="teamsiteConfig.color">
          {{ signupBtnText }}
        </mq-link>
      </div>
    </div>

    <div class="row">
      <div class="columns medium-12">
        <ul class="constraints left">
          <li v-for="(priceSlot, index) in displayedTariffSlots" :key="index" v-if="priceSlot.constraints">
            {{ footnoteNumber(priceSlot) }}) {{ priceSlot.constraints }}
          </li>
        </ul>
      </div>

      <div class="columns medium-12">
        <div v-if="anyNetPrice" class="vat-hint">
          * {{ $t('pricing.net_hint', {vat: vatRate}) }}
        </div>
        <div v-else class="vat-hint">
          {{ $t('pricing.gross_hint', {vat: vatRate}) }}
        </div>
      </div>

    </div>

  </div>
</template>

<style lang="scss" scoped>
span.expand-tariff {
  transition: none;
  display: inline-block;
  margin-top: rem-calc(40);
  margin-bottom: rem-calc(20);
  cursor: pointer;

  .text {
    color: $white;
    display: inline-block;
    font-weight: $font-weight-medium;
    font-size: $font-size-small;
    padding: 0.24em 0.5em;
    text-transform: none;
    letter-spacing: 0.15em;
  }

  .arrow {
    transition: top 250ms;
    font-size: 2rem;
    position: relative;
    top: 0.0rem;
    font-size: rem-calc(28);
  }

  &.hover-down {
    .arrow {
      top: 1rem;
    }
  }

  &.hover-up {
    .arrow {
      top: -1rem;
    }
  }
}

a.moqo_link {
  &--tiny {
    font-size: rem-calc(13);
    padding: 0;
    margin-bottom: 0;
    margin-top: -10px;
    max-width: 999px;
  }
}
.car-cta {
  background-color: #363636;
  color: white;
  &>.column {
    padding: 20px 30px;
  }
}
.pricing {
  background-color: white;
  margin-top: rem-calc(5);

  &.padding {
    padding-bottom: rem-calc(35);
  }
}

.constraints-hint {
  margin-top: rem-calc(35);
  font-size: rem-calc(12);
}

.vat-hint {
  text-align: right;
  font-size: rem-calc(12);
  margin: 5px;
}

ul.constraints {
  list-style-type: none;
  padding-left: 0;
  margin-top: 5px;
  li {
    font-size: rem-calc(12);
    line-height: rem-calc(12);
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Pricing.vue